/**
 * Shared Adam undo session — one history for tracker + project + arrange + instruments.
 * Bridges register late (PaintController mounts before labRuntime).
 */
import type { AdamCommandContext, AdamCommandResult, AdamSelectionState } from "./Command";
import { AdamCommandHost } from "./AdamCommandHost";
import type { AdamProjectBridge } from "./projectBridge";
import type { AdamArrangeBridge } from "./arrangeBridge";
import type { AdamInstrumentBridge } from "./instrumentBridge";
import type { AdamSampleBridge } from "./sampleBridge";
import { CELL_WIDTH } from "../lib/trackerCell";

type TrackerBridge = {
  createContext: () => AdamCommandContext;
  commit: (result: AdamCommandResult, selection: AdamSelectionState | undefined) => void;
  /** Flush in-progress typing/recording before undo/redo. */
  prepareHistoryNav?: () => void;
};

const bridges: {
  tracker?: TrackerBridge;
  project?: AdamProjectBridge;
  arrange?: AdamArrangeBridge;
  instruments?: AdamInstrumentBridge;
  samples?: AdamSampleBridge;
} = {};

let host: AdamCommandHost | null = null;

function emptyTrackerContext(): AdamCommandContext {
  const empty = ".".repeat(CELL_WIDTH);
  return {
    getCell: () => empty,
    setCell: () => {},
    getTrackName: () => undefined,
    setTrackName: () => {},
    getRowMarker: () => undefined,
    setRowMarker: () => {},
  };
}

/** Single shared host (created lazily). */
export function getAdamCommandHost(): AdamCommandHost {
  if (!host) {
    host = new AdamCommandHost({
      createContext: () => {
        const base = bridges.tracker?.createContext() ?? emptyTrackerContext();
        return {
          ...base,
          project: bridges.project,
          arrange: bridges.arrange,
          instruments: bridges.instruments,
          samples: bridges.samples,
        };
      },
      commit: (result, selection) => {
        bridges.tracker?.commit(result, selection);
        bridges.project?.afterCommit?.(result);
        bridges.arrange?.afterCommit?.(result);
        bridges.instruments?.afterCommit?.(result);
        bridges.samples?.afterCommit?.(result);
      },
    });
  }
  return host;
}

export function registerAdamTrackerBridge(bridge: TrackerBridge | null): void {
  bridges.tracker = bridge ?? undefined;
}

export function registerAdamProjectBridge(bridge: AdamProjectBridge | null): void {
  bridges.project = bridge ?? undefined;
}

export function registerAdamArrangeBridge(bridge: AdamArrangeBridge | null): void {
  bridges.arrange = bridge ?? undefined;
}

export function registerAdamInstrumentBridge(bridge: AdamInstrumentBridge | null): void {
  bridges.instruments = bridge ?? undefined;
}

export function registerAdamSampleBridge(bridge: AdamSampleBridge | null): void {
  bridges.samples = bridge ?? undefined;
}

export function clearAdamSessionHistory(): void {
  host?.clear();
}

/** Global ⌘Z — flush tracker coalesce, then undo. */
export function adamUndo(): AdamCommandResult | null {
  bridges.tracker?.prepareHistoryNav?.();
  return getAdamCommandHost().undo();
}

/** Global ⌘⇧Z / ⌘Y. */
export function adamRedo(): AdamCommandResult | null {
  bridges.tracker?.prepareHistoryNav?.();
  return getAdamCommandHost().redo();
}
